/**
 * texture_share — one GPU texture per distinct KTX2 image across LOD levels
 * (LOD-switch research S4, 2026-09-02).
 *
 * Every cooked level file carries its own copies of a building's textures
 * (LOD1 and LOD2 at 512 px are byte-identical; LOD3's 256 px chain is a
 * truncation of the same), so a LOD1 → LOD2 swap re-uploaded textures the
 * GPU already held. A registry keyed by the image's level-0 bytes (two FNV-1a
 * passes + dimensions/format/mip count) hands every material the FIRST
 * texture seen for that key; duplicates are dropped before they ever upload.
 * Shared textures are refcounted per scene and disposed only when the last
 * scene referencing them is disposed — the asset cache MUST dispose through
 * `disposeAssetScene`, never the engine's plain disposer.
 */
import * as THREE from 'three/webgpu';

const SLOTS = ['map', 'normalMap', 'emissiveMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'alphaMap', 'specularMap', 'bumpMap', 'displacementMap'] as const;
interface Entry { tex: THREE.Texture; users: number; bytes: number }
const registry = new Map<string, Entry>();
export const textureShareStats = { registry: 0, shared: 0, dedupedBytes: 0, disposed: 0 };

function fnv(a: Uint8Array, seed: number, stride: number): number {
  let h = seed >>> 0;
  for (let i = 0; i < a.length; i += stride) h = Math.imul(h ^ a[i], 16777619) >>> 0;
  return h;
}

/** The share key of a compressed texture (null for anything else — uncompressed textures are rare and small here). */
export function textureKey(t: THREE.Texture): string | null {
  const ct = t as unknown as { isCompressedTexture?: boolean; mipmaps?: Array<{ data?: ArrayBufferView; width?: number; height?: number }>; format?: number; image?: { width?: number; height?: number } };
  if (!ct.isCompressedTexture || !Array.isArray(ct.mipmaps) || ct.mipmaps.length === 0) return null;
  const m0 = ct.mipmaps[0];
  const d = m0.data;
  if (!d) return null;
  const bytes = new Uint8Array(d.buffer, d.byteOffset, d.byteLength);
  const w = m0.width ?? ct.image?.width ?? 0, h = m0.height ?? ct.image?.height ?? 0;
  return `${w}x${h}:${ct.format ?? 0}:${ct.mipmaps.length}:${bytes.length}:${fnv(bytes, 2166136261, 1).toString(16)}:${fnv(bytes, 0x9e3779b9, 7).toString(16)}`;
}

function texBytes(t: THREE.Texture): number {
  const ct = t as unknown as { mipmaps?: Array<{ data?: ArrayBufferView }> };
  let b = 0; for (const m of ct.mipmaps ?? []) b += m.data?.byteLength ?? 0; return b;
}

/** Replace this scene's compressed textures with the registry's shared instances. Idempotent per scene. */
export function shareSceneTextures(scene: THREE.Object3D): { shared: number; textures: number } {
  const ud = scene.userData as { shareKeys?: Set<string> };
  if (ud.shareKeys) return { shared: 0, textures: 0 };
  const keys = new Set<string>();
  const replaced = new Map<THREE.Texture, THREE.Texture>();
  let shared = 0, textures = 0;
  scene.traverse((o) => {
    const mats = (o as THREE.Mesh).material;
    for (const mat of Array.isArray(mats) ? mats : mats ? [mats] : []) {
      const rec = mat as unknown as Record<string, unknown>;
      for (const slot of SLOTS) {
        const t = rec[slot] as THREE.Texture | null | undefined;
        if (!t || !(t as { isTexture?: boolean }).isTexture) continue;
        let out = replaced.get(t);
        if (!out) {
          const key = textureKey(t);
          if (!key) continue;
          textures++;
          const e = registry.get(key);
          if (e && e.tex !== t) {
            out = e.tex; shared++; textureShareStats.shared++; textureShareStats.dedupedBytes += e.bytes;
            // LOD0 shape/skin split (L1): the skin refs ride on the shape file's
            // image extras; whichever object the registry keeps must carry them
            // (LOD1 usually loads first and has none).
            const skin = (t.userData as { atlasSkin?: unknown }).atlasSkin;
            if (skin && !(out.userData as { atlasSkin?: unknown }).atlasSkin) (out.userData as { atlasSkin?: unknown }).atlasSkin = skin;
            t.dispose(); // never uploaded: only the JS-side arrays go
          } else {
            if (!e) { registry.set(key, { tex: t, users: 0, bytes: texBytes(t) }); textureShareStats.registry = registry.size; }
            out = t;
          }
          (out.userData as Record<string, unknown>).shareKey = key;
          replaced.set(t, out);
          keys.add(key);
        }
        if (out !== t) rec[slot] = out;
      }
    }
  });
  for (const k of keys) { const e = registry.get(k); if (e) e.users++; }
  ud.shareKeys = keys;
  return { shared, textures };
}

/** Drop this scene's references; a texture nobody references any more is disposed. */
export function releaseSceneTextures(scene: THREE.Object3D): void {
  const ud = scene.userData as { shareKeys?: Set<string> };
  if (!ud.shareKeys) return;
  for (const k of ud.shareKeys) {
    const e = registry.get(k);
    if (!e) continue;
    if (--e.users <= 0) { e.tex.dispose(); registry.delete(k); textureShareStats.disposed++; textureShareStats.registry = registry.size; }
  }
  ud.shareKeys = undefined;
}

/** Shared-aware disposer for the asset cache: releases shared textures, disposes everything else. */
export function disposeAssetScene(obj: THREE.Object3D): void {
  releaseSceneTextures(obj);
  const seenMat = new Set<THREE.Material>();
  obj.traverse((child) => {
    const c = child as THREE.Mesh;
    if (c.geometry) c.geometry.dispose();
    const mats = Array.isArray(c.material) ? c.material : c.material ? [c.material] : [];
    for (const mat of mats) {
      if (seenMat.has(mat)) continue;
      seenMat.add(mat);
      const rec = mat as unknown as Record<string, unknown>;
      for (const key of Object.keys(rec)) {
        const v = rec[key] as THREE.Texture | undefined;
        if (v && (v as { isTexture?: boolean }).isTexture && !(v.userData as { shareKey?: string }).shareKey) v.dispose();
      }
      mat.dispose();
    }
  });
}

/** Registry size + users (probe). */
export function textureShareDiag(): { entries: number; users: number; stats: typeof textureShareStats } {
  let users = 0; for (const e of registry.values()) users += e.users;
  return { entries: registry.size, users, stats: { ...textureShareStats } };
}
